import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Shield, CheckCircle, Loader2 } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

interface PasswordChangeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentPassword: string;
  setCurrentPassword: (pw: string) => void;
  newPassword: string;
  setNewPassword: (pw: string) => void;
  confirmPassword: string;
  setConfirmPassword: (pw: string) => void;
  loading: boolean;
  error: string | null;
  done: boolean;
  onSubmit: () => void;
  onReset: () => void;
}

export const PasswordChangeDialog = ({
  open, onOpenChange,
  currentPassword, setCurrentPassword,
  newPassword, setNewPassword,
  confirmPassword, setConfirmPassword,
  loading, error, done,
  onSubmit, onReset,
}: PasswordChangeDialogProps) => {
  const { t } = useLanguage();

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onReset(); else onOpenChange(true); }}>
      <DialogContent className="sm:max-w-[450px]" data-testid="password-change-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-pink-600" />
            {t('passwordChange.title')}
          </DialogTitle>
          <DialogDescription>{t('passwordChange.description')}</DialogDescription>
        </DialogHeader>

        {!done ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="current-password">{t('passwordChange.currentPassword')}</Label>
              <Input id="current-password" type="password" value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder={t('passwordChange.currentPlaceholder')} data-testid="current-password-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-password">{t('passwordChange.newPassword')}</Label>
              <Input id="new-password" type="password" value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder={t('passwordChange.minChars')} data-testid="new-password-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">{t('passwordChange.confirmPassword')}</Label>
              <Input id="confirm-password" type="password" value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t('passwordChange.confirmPlaceholder')} data-testid="confirm-password-input" />
            </div>
            {error && <p className="text-sm text-red-500" data-testid="password-change-error">{error}</p>}
          </div>
        ) : (
          <div className="text-center py-4" data-testid="password-change-success">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <p className="text-green-700 font-medium">{t('passwordChange.passwordChanged')}</p>
            <p className="text-sm text-gray-500 mt-2">{t('passwordChange.onlyYourAccount')}</p>
          </div>
        )}

        <DialogFooter>
          {!done ? (
            <>
              <Button variant="outline" onClick={onReset}>{t('common.cancel')}</Button>
              <Button onClick={onSubmit} disabled={loading || !currentPassword || !newPassword || !confirmPassword} data-testid="submit-password-change-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {t('passwordChange.submit')}
              </Button>
            </>
          ) : (
            <Button onClick={onReset} className="w-full" data-testid="password-change-done-btn">{t('common.done')}</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
